import { useEffect, type JSX, type ReactNode } from "react";

import { useAuth } from "../../providers/auth-provider.js";
import { LoadingState } from "../data/LoadingState.js";

interface RouteGuardProps {
  children: ReactNode;
}

/**
 * Wraps a protected route subtree. Renders LoadingState while auth is
 * bootstrapping; redirects to /login (preserving returnTo) when unauthenticated.
 *
 * Uses a FULL navigation (window.location), not react-router <Navigate>, so the
 * redirect escapes the app router's `/t/<code>` basename back to the root
 * /login page. `window.location.pathname` already includes the tenant slug, so
 * returnTo round-trips correctly.
 */
export function RouteGuard({ children }: RouteGuardProps): JSX.Element {
  const { user, loading } = useAuth();
  const unauthed = !loading && !user;

  useEffect(() => {
    if (unauthed) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/login?returnTo=${returnTo}`);
    }
  }, [unauthed]);

  if (loading || unauthed) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingState variant="spinner" message="Checking session…" />
      </div>
    );
  }

  return <>{children}</>;
}
